import React from "react";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import {
  ScrollToTopOnMount,
  useQueryGetCourse,
  useQueryGetCourseUnits,
} from "@examspark/common-app";
import { subjectMeta } from "@examspark/common-meta";
import { RouterLink, Tabs, VSpace } from "@examspark/common-ui";
import { Resource } from "@examspark/types";

import BrowseList from "../../components/browse/BrowseList";
import ClassroomLayout from "../../components/classroom/ClassroomLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import Loading from "../../components/common/Loading";
import PageHead from "../../components/common/PageHead";
import PageTitle from "../../components/common/PageTitle";
import UnitContent from "../../components/common/UnitContent";
import CourseDocuments from "../../components/course/CourseDocuments";
import CourseResources from "../../components/course/CourseResources";
import ResourceTools from "../../components/resources/ResourceTools";
import { AsideLayoutProvider } from "../../contexts/AsideLayoutContext";

const PageSections = styled(VSpace)``;
const PageSection = styled(VSpace)`
  gap: 1rem;
`;
const NoResults = styled.div`
  color: #777;
`;

const ClassroomAside = styled.aside`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999;
`;

const CourseRoute = () => {
  const params = useParams<{
    id: string;
  }>();
  const { data: course } = useQueryGetCourse(params.id);
  const { data: units } = useQueryGetCourseUnits(params.id);
  const match = useMatch("/course/:id/:tab");
  const navigate = useNavigate();
  const subject = course?.subject?.[0];
  const [requestAside, setRequestAside] = React.useState<
    Record<string, boolean>
  >({});

  return (
    <AsideLayoutProvider
      requestAside={requestAside}
      onRequestAside={(id, request) => {
        setRequestAside((prev) => ({ ...prev, [id]: request }));
      }}
      asidePortalId="classroom-aside"
      breakAt={"1600px"}
    >
      <ScrollToTopOnMount />
      <ClassroomLayout
        main={
          <PageSections>
            <PageHead>
              <Breadcrumbs
                items={[
                  ...Breadcrumbs.Dashboard(),
                  <RouterLink key="courses" to="/browse">
                    Courses
                  </RouterLink>,
                  subject ? (
                    <RouterLink key="subject" to="/browse">
                      {
                        subjectMeta[
                          course?.subject?.[0] as keyof typeof subjectMeta
                        ]?.label
                      }
                    </RouterLink>
                  ) : null,
                ]}
              />
              <PageTitle tools={<ResourceTools resource={course} />}>
                {course?.name ?? "Loading Course..."}
              </PageTitle>
            </PageHead>
            {course?.description && (
              <UnitContent content={course?.description} />
            )}
            <Tabs
              value={match?.params.tab ?? "overview"}
              onChange={(value) => navigate(value === "overview" ? "." : value)}
            >
              <Tabs.TabItem value="overview">Units / Modules</Tabs.TabItem>
              <Tabs.TabItem value="resources">Resources</Tabs.TabItem>
            </Tabs>
            {(match?.params.tab ?? "overview") === "overview" && (
              <PageSection>
                {course == null && (
                  <Loading>Loading units for this course...</Loading>
                )}
                {course != null && !units?.length && (
                  <NoResults>
                    There are no units listed for this course yet.
                  </NoResults>
                )}
                {course != null && !!units?.length && (
                  <BrowseList
                    data={units?.map((dataUnit) => dataUnit as Resource)}
                  />
                )}
              </PageSection>
            )}
            {match?.params.tab === "resources" && (
              <CourseResources course={course} />
            )}
          </PageSections>
        }
        side={<CourseDocuments course={course} />}
      />
      <ClassroomAside id="classroom-aside" />
    </AsideLayoutProvider>
  );
};

export default CourseRoute;
